import { apiRequest } from '../../api/client';

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

/** Các ô số đầu màn `a-dash` cho một ngày (S7-BE-03, AC-24). */
export interface DashboardOverview {
  businessDate: string;
  memberRevenue: number;
  memberRevenuePreviousDay: number;
  /** Chênh so với hôm trước, %. null khi hôm trước = 0. */
  memberRevenueDeltaPercent: number | null;
  pointsIssued: number;
  pointsRedeemed: number;
  /** Tỷ lệ đốt = tiêu / phát ra. null khi chưa phát điểm nào. */
  burnRatePercent: number | null;
  /** Số hóa đơn thành viên được chấp nhận — TỬ SỐ của KR1. */
  memberBillCount: number;
  /**
   * KR1 "% hóa đơn có mã" — LUÔN null ở Phase 1: mẫu số cần tổng hóa đơn POS mà hệ thống
   * đứng độc lập khỏi POS (ADR-004). Hiện `memberBillCount` + ghi chú thay vì một % bịa.
   */
  memberBillRatePercent: number | null;
  memberBillRateNote: string;
  manualScansPending: number;
  reversalPendingScans: number;
  flaggedScans: number;
  /** Mốc làm mới gần nhất; null khi ngày này chưa có dòng tổng hợp nào. */
  refreshedAt: string | null;
}

export interface DashboardTrendPoint {
  date: string;
  memberRevenue: number;
  pointsIssued: number;
  pointsRedeemed: number;
}

export interface DashboardRevenueTrend {
  from: string;
  to: string;
  points: DashboardTrendPoint[];
}

export interface TierDistributionBucket {
  /** null ở bậc "chưa xếp hạng". */
  tierCode: string | null;
  tierName: string;
  memberCount: number;
  percent: number;
}

export interface TierDistribution {
  totalMembers: number;
  buckets: TierDistributionBucket[];
}

export interface BranchPerformanceRow {
  branchId: string;
  branchName: string;
  chainName: string;
  memberRevenue: number;
  memberBillCount: number;
  flaggedScans: number;
}

export interface BranchPerformance {
  businessDate: string;
  rows: BranchPerformanceRow[];
}

/** Bóc tách điểm tiêu được theo loại bút toán (S9-BE-01, AC-24). */
export interface LiabilityBreakdownItem {
  /** Tên PointEntryType, ví dụ "Earn", "WelcomeBonus", "Redeem". */
  entryType: string;
  /** Tổng điểm tiêu được của loại này (dương = phát ra, âm = đốt/hết hạn). */
  spendablePoints: number;
}

/**
 * Earn/burn liability TOÀN HỆ THỐNG (S9-BE-01, AC-24) — điểm tồn cộng dồn trọn đời. Không có phạm vi
 * chi nhánh: ví điểm dùng chung mọi chuỗi (ADR-005). Không có phễu referral: M08 hoãn Phase 1.
 */
export interface DashboardLiability {
  totalIssued: number;
  totalBurned: number;
  /** Tỷ lệ đốt trọn đời = đốt / phát ra. null khi chưa phát điểm nào. */
  burnRatePercent: number | null;
  /** Điểm tồn tính từ sổ cái = phát ra − đốt. */
  outstandingFromLedger: number;
  /** Điểm tồn tính từ bản chiếu số dư (member_balances). */
  outstandingFromBalances: number;
  /** Chênh giữa hai cách tính. 0 là khớp; khác 0 là projector lệch. */
  discrepancy: number;
  /** Hai đường tính có khớp không (AC-8/AC-24). */
  balanceMatchesLedger: boolean;
  membersWithBalance: number;
  breakdown: LiabilityBreakdownItem[];
}

/** Phạm vi chung của các endpoint dashboard. Ngày theo lịch VN (yyyy-MM-dd). */
export interface DashboardScope {
  date?: string;
  chainId?: string;
  branchId?: string;
}

const scopeParams = (scope: DashboardScope): URLSearchParams => {
  const params = new URLSearchParams();
  if (scope.date) params.set('date', scope.date);
  if (scope.chainId) params.set('chainId', scope.chainId);
  if (scope.branchId) params.set('branchId', scope.branchId);
  return params;
};

const query = (params: URLSearchParams) => {
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const dashboardKeys = {
  overview: (scope: DashboardScope) => ['dashboard', 'overview', scope] as const,
  trend: (scope: DashboardScope, days: number) => ['dashboard', 'trend', scope, days] as const,
  tiers: ['dashboard', 'tier-distribution'] as const,
  branches: (scope: DashboardScope) => ['dashboard', 'branch-performance', scope] as const,
  liability: ['dashboard', 'liability'] as const,
};

export const dashboardApi = {
  overview: (scope: DashboardScope) =>
    unwrap(apiRequest<DashboardOverview>(`/admin/dashboard/overview${query(scopeParams(scope))}`)),

  revenueTrend: (scope: DashboardScope, days = 7) => {
    const params = scopeParams(scope);
    // `date` của scope không dùng cho trend; endDate mặc định hôm nay ở server.
    params.delete('date');
    params.set('days', String(days));
    return unwrap(apiRequest<DashboardRevenueTrend>(`/admin/dashboard/revenue-trend${query(params)}`));
  },

  tierDistribution: () =>
    unwrap(apiRequest<TierDistribution>('/admin/dashboard/tier-distribution')),

  branchPerformance: (scope: DashboardScope) =>
    unwrap(
      apiRequest<BranchPerformance>(`/admin/dashboard/branch-performance${query(scopeParams(scope))}`),
    ),

  liability: () => unwrap(apiRequest<DashboardLiability>('/admin/dashboard/liability')),
};
